import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Loader from 'components/Loader';
import OrderApiService from 'api/OrderApiService';

interface VersionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  onDownloadVersion: (versionId: number) => void;
}

interface Version {
  id: number;
  version: number;
  date: string;
  created_at: string;
  name: string;
  display_name: string;
  is_latest: boolean;
}

interface VersionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  onDownloadVersion: (versionId: number) => void;
  versions: Version[];
}

const VersionHistoryDialog: React.FC<VersionHistoryDialogProps> = ({
  open,
  onClose,
  orderId,
  onDownloadVersion,
  versions,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [t] = useTranslation();

  const userLocale = navigator.language || 'fr-FR';

  // Fetch versions when dialog opens
  // useEffect(() => {
  //   if (open && orderId) {
  //     fetchVersions();
  //   }
  // }, [open, orderId]);

  // const fetchVersions = async () => {
  //   setLoading(true);
  //   try {
  //     const { versions } = await OrderApiService.getOrderBaselineVersions(orderId);
  //     setVersions(versions || []);
  //   } catch (error) {
  //     console.error('Failed to fetch versions:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const filteredVersions = versions.filter(
    (version) =>
      version.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      version.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(userLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // changed date to created_at
  const formatTime = (dateString: string | null) => {
    if (!dateString) return 'N/A'; // Handle null/undefined dates
    return new Date(dateString).toLocaleTimeString(userLocale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('version_history')}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder={t('Search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#90caf9', // Light blue border
              },
              '&:hover fieldset': {
                borderColor: '#64b5f6', // Slightly darker on hover
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2', // Dark blue when focused
                borderWidth: '2px',
              },
            },
          }}
        />

        {/* Loading State */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Loader size={40} />
          </Box>
        ) : (
          /* Versions List */
          <List>
            {filteredVersions.map((version) => (
              <ListItem
                key={version.id}
                divider
                sx={{
                  border: '1px solid #90caf9',
                  mb: 1,
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {version.display_name}
                      </Typography>
                      {version.is_latest && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.7rem',
                          }}
                        >
                          {t('Latest')}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={`${formatDate(version.created_at)}, ${formatTime(version.created_at)}`}
                />
                <ListItemSecondaryAction>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => onDownloadVersion(version.version)}
                    sx={{ minWidth: '100px' }}
                  >
                    {t('Download')}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}

            {filteredVersions.length === 0 && !loading && (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  {searchTerm ? t('No versions found matching your search.') : t('No versions available.')}
                </Typography>
              </Box>
            )}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistoryDialog;
